import {defineStore} from "pinia";
import ProjectAPI from "@/api/ProjectAPI";
import { StorageSerializers, useStorage } from '@vueuse/core';
import {ref} from "vue";
import type {Project} from "@/models/Project";
import {useBidPackageStore} from "@/stores/BidPackageStore";
import {createRequestDeduper} from '@/use/createRequestDeduper';

export const useProjectStore = defineStore('project', () => {
    const isLoading = ref<boolean>(false);
    const viewBidPackageId = ref<number | null>(null);

    const project = useStorage<Project | undefined>('project', null, sessionStorage, {
        serializer: StorageSerializers.object,
    });

    const bidPackageStore = useBidPackageStore();
    const dedupeRequest = createRequestDeduper();

    const clear = () => {
        project.value = null;
        setViewBidPackageId(null);
    };

    const setViewBidPackageId = (bidPackageId: number | null) => viewBidPackageId.value = bidPackageId;

    const show = async (projectId: number, payload: any = {params: null}) => {
        const { params } = payload;
        const key = `project:${projectId}:${JSON.stringify(params)}`;

        return dedupeRequest(key, async () => {
            isLoading.value = true;
            if (Number(project.value?.id) !== projectId) {
                bidPackageStore.clear();
            }

            await new ProjectAPI().show(projectId, params)
                .then(data => {
                    project.value = data;
                })
                .finally(() => isLoading.value = false);
        });
    }

    const acceptNda = async (projectId: number, ndaId: number) => {
        return await new ProjectAPI().acceptNda(projectId, { nda_id: ndaId });
    };

    return {
        project,
        isLoading,
        viewBidPackageId,
        setViewBidPackageId,
        clear,
        show,
        acceptNda,
    };
});
